import React from "react";
import { Info } from "../../icons";
/* Tooltip + InfoIcon — Phase B primitives.
   Consolidates 73 native title= attrs + legacy InfoIcon + legacy Tooltip wrapper.
   Note: DealPopover is a different beast (click-to-open large card) and stays separate.

   Behavior:
     - Open delay 400ms (hover) / immediate on focus
     - Close delay 80ms
     - Position: above by default, flips below if no space
     - Max-width 280px, --fs-body-sm, --surface-elevated
     - Escape dismisses; aria-describedby links trigger ↔ tip

   Phase 2 Atoms — InfoIcon's `ⓘ` Unicode glyph replaced with the vendored
   Lucide Info SVG (src/icons/Info.tsx). Same visual role, currentColor +
   crisp at any size, no font-fallback variance.
*/

const { useState, useRef, useEffect, useId } = React;

export interface TooltipProps {
  tip?: React.ReactNode;
  placement?: "auto" | "above" | "below";
  className?: string;
  children: React.ReactNode;
}

export default function Tooltip({ tip, placement = "auto", className = "", children }: TooltipProps): any {
  if (!tip) return children;
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState("above");
  const timerRef = useRef(null);
  const wrapRef = useRef(null);
  const id = (typeof useId === "function") ? useId() : `tt-${Math.random().toString(36).slice(2, 9)}`;

  const computePos = () => {
    if (placement !== "auto") { setPos(placement); return; }
    try {
      const rect = wrapRef.current?.getBoundingClientRect();
      if (!rect) return;
      const TIP_H = 100;
      setPos(rect.top < TIP_H + 8 ? "below" : "above");
    } catch { setPos("above"); }
  };

  const handleEnter = () => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => { computePos(); setOpen(true); }, 400);
  };
  const handleLeave = () => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setOpen(false), 80);
  };
  const handleFocus = () => { computePos(); setOpen(true); };
  const handleBlur = () => setOpen(false);

  useEffect(() => () => clearTimeout(timerRef.current), []);
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <span
      ref={wrapRef}
      className={`v2-tip-wrap v2-tip-wrap--${pos} ${open ? "v2-tip-wrap--open" : ""} ${className}`.trim()}
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
      onFocus={handleFocus}
      onBlur={handleBlur}
    >
      {React.Children.map(children, (c: any) => {
        if (!React.isValidElement(c)) return c;
        return React.cloneElement(c as any, { "aria-describedby": open ? id : undefined });
      })}
      <span className="v2-tip" role="tooltip" id={id} aria-hidden={!open}>
        {tip}
      </span>
    </span>
  );
}

export function InfoIcon({ tip, className = "", ariaLabel = "info" }: { tip?: React.ReactNode; className?: string; ariaLabel?: string }) {
  if (!tip) return null;
  return (
    <Tooltip tip={tip} className={className}>
      <span
        className="v2-info-icon"
        tabIndex={0}
        role="button"
        aria-label={ariaLabel}
      >
        <Info size="1em" strokeWidth={2} />
      </span>
    </Tooltip>
  );
}
